import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import './scss/myAccount.scss';
import api from './services/apiService';
import { FaCrown } from 'react-icons/fa';
import { authService } from './services/authService';
import { biometricService } from './services/biometricService';
import { Capacitor } from '@capacitor/core';

function MyAccount() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [isDisablingBiometric, setIsDisablingBiometric] = useState(false);
  const teamType = sessionStorage.getItem('selected_team_type');
  const isAcademyPlayer = teamType === 'academy';

  useEffect(() => {
    const userType = sessionStorage.getItem('user_type');
    // Only fetch subscription status for parents and players who are not admins
    if (['parent', 'player'].includes(userType) && !authService.isAdmin()) {
      fetchSubscriptionStatus();
    }
    
    // Check biometric status
    checkBiometricStatus();
  }, []);

  const checkBiometricStatus = async () => {
    if (Capacitor.isNativePlatform()) {
      try {
        const available = await biometricService.isAvailable();
        setBiometricAvailable(available);
        
        if (available) {
          const hasCredentials = await authService.canUseBiometricLogin();
          const isEnabled = authService.isBiometricEnabled();
          setBiometricEnabled(hasCredentials || isEnabled);
        }
      } catch (error) {
        console.error('Error checking biometric status:', error);
        setBiometricAvailable(false);
      }
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const teamId = sessionStorage.getItem('selected_team_id');
      const url = teamId 
        ? `${process.env.REACT_APP_API_URL}/get_subscription_status.php?team_id=${teamId}`
        : `${process.env.REACT_APP_API_URL}/get_subscription_status.php`;
      
      const response = await api.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.data.success) {
        setSubscriptionStatus(response.data);
      }
    } catch (error) {
      console.error('Error fetching subscription status:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const renderSubscriptionStatus = () => {
    if (!subscriptionStatus) return null;

    const userType = sessionStorage.getItem('user_type');
    // Don't show subscription status for admins or non-parent/player users
    if (!['parent', 'player'].includes(userType) || authService.isAdmin()) return null;

    return (
      <div className="subscription-status">
        {subscriptionStatus.in_trial ? (
          <div className="trial-status">
            <h2>Trial Status</h2>
            <div className="status-content">
              <div className="status-icon">🎉</div>
              <div className="status-details">
                <p>You are currently in your 14-day trial period.</p>
                <p className="highlight">{subscriptionStatus.trial_days_remaining} days remaining</p>
                {subscriptionStatus.trial_days_remaining <= 7 && (
                  <button 
                    className="upgrade-button"
                    onClick={() => navigate('/pro-features')}
                  >
                    Upgrade to Pro
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : subscriptionStatus.subscription ? (
          <div className={`pro-status ${subscriptionStatus.subscription.status}`}>
            <h2>Pro Subscription <FaCrown className="crown-icon" /></h2>
            <div className="status-content">
              <div className="status-details">
                <p>
                  <strong>Type:</strong> {subscriptionStatus.subscription.type === 'monthly' ? 'Monthly' : 'Annual'} Pro
                </p>
                <p>
                  <strong>Amount:</strong> {subscriptionStatus.subscription.display_amount} per {subscriptionStatus.subscription.interval}
                </p>
                <p>
                  <strong>Status:</strong> {getStatusDisplay(subscriptionStatus.subscription.status)}
                </p>
                {subscriptionStatus.subscription.status === 'past_due' && (
                  <p className="warning">
                    Your last payment failed. Please update your payment method to continue your subscription.
                  </p>
                )}
                {subscriptionStatus.subscription.status === 'incomplete' && (
                  <p className="warning">
                    Your subscription setup is incomplete. Please complete the payment setup to activate your subscription.
                  </p>
                )}
                {subscriptionStatus.subscription.status === 'incomplete_expired' && (
                  <p className="error">
                    Your subscription setup has expired. Please start a new subscription.
                  </p>
                )}
                <p>
                  <strong>Start Date:</strong> {formatDate(subscriptionStatus.subscription.start_date)}
                </p>
                {subscriptionStatus.subscription.end_date && (
                  <p>
                    <strong>End Date:</strong> {formatDate(subscriptionStatus.subscription.end_date)}
                  </p>
                )}
                {subscriptionStatus.subscription.days_remaining && (
                  <p className={`highlight ${subscriptionStatus.subscription.status === 'cancelling' ? 'warning' : ''}`}>
                    {subscriptionStatus.subscription.days_remaining} days remaining
                  </p>
                )}
                {subscriptionStatus.subscription.cancellation_message && (
                  <p className="warning">
                    {subscriptionStatus.subscription.cancellation_message}
                  </p>
                )}
                {subscriptionStatus.stripe_portal_url && (
                  <a 
                    href={subscriptionStatus.stripe_portal_url}
                    className="manage-subscription-button"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Manage Subscription
                  </a>
                )}
                {(subscriptionStatus.subscription.status === 'incomplete' || 
                  subscriptionStatus.subscription.status === 'past_due' || 
                  subscriptionStatus.subscription.status === 'incomplete_expired') && (
                  <button 
                    className="upgrade-button"
                    onClick={() => navigate('/pro-features')}
                  >
                    Update Payment Method
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="no-subscription">
            <h2>Subscription Status</h2>
            <div className="status-content">
              <p>You currently don't have an active subscription.</p>
              <button 
                className="upgrade-button"
                onClick={() => navigate('/pro-features')}
              >
                Upgrade to Pro
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const getStatusDisplay = (status) => {
    const statusMap = {
      'active': 'Active',
      'cancelling': 'Cancelling',
      'cancelled': 'Cancelled',
      'past_due': 'Payment Past Due',
      'incomplete': 'Setup Incomplete',
      'incomplete_expired': 'Setup Expired',
      'expired': 'Expired'
    };
    return statusMap[status] || status;
  };

  const handleEmailUpdate = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.post(`${process.env.REACT_APP_API_URL}/update_email.php`, {
        email: email
      });

      if (response.data.success) {
        setSuccess('Email updated successfully');
        setEmail('');
      } else {
        setError(response.data.message || 'Failed to update email');
      }
    } catch (error) {
      setError(error.response?.data?.message || 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhoneUpdate = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.post(`${process.env.REACT_APP_API_URL}/update_phone.php`, {
        phone: phone
      });

      if (response.data.success) {
        setSuccess('Phone number updated successfully');
        setPhone('');
      } else {
        setError(response.data.message || 'Failed to update phone number');
      }
    } catch (error) {
      setError(error.response?.data?.message || 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      setIsLoading(false);
      return;
    }

    try {
      const response = await api.post(`${process.env.REACT_APP_API_URL}/update_password.php`, {
        current_password: currentPassword,
        new_password: newPassword
      });

      if (response.data.success) {
        setSuccess('Password updated successfully');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setError(response.data.message || 'Failed to update password');
      }
    } catch (error) {
      setError(error.response?.data?.message || 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisableBiometric = async () => {
    if (!window.confirm('Are you sure you want to disable Face ID login? You will need to enter your phone number and password to sign in.')) {
      return;
    }

    setIsDisablingBiometric(true);
    setError('');
    setSuccess('');

    try {
      await authService.disableBiometricLogin();
      setBiometricEnabled(false);
      setSuccess('Face ID login has been disabled. You can enable it again after your next login.');
    } catch (error) {
      console.error('Error disabling biometric:', error);
      setError('Failed to disable Face ID. Please try again.');
    } finally {
      setIsDisablingBiometric(false);
    }
  };



  return (
    <div className="my-account">
      <Header />
      <div className="container">
        <button className="back-button" onClick={() => window.history.back()}>← Back</button>
        <h1>My Account</h1>
        
        {!authService.isChild() && renderSubscriptionStatus()}
        
        {error && <div className="error-message">{error}</div>}

        {!authService.isChild() && (
          <div className="account-section">
            <h2>Update Email</h2>
          <form onSubmit={handleEmailUpdate}>
            <div className="form-group">
              <label htmlFor="email">New Email Address</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <button type="submit" disabled={isLoading}>
              {isLoading ? 'Updating...' : 'Update Email'}
            </button>
          </form>
          {success && success.includes('Email') && (
            <div className="success-message">{success}</div>
          )}
        </div>
        )}

        {!authService.isChild() && !isAcademyPlayer && (
          <div className="account-section">
            <h2>Update Phone Number</h2>
          <form onSubmit={handlePhoneUpdate}>
            <div className="form-group">
              <label htmlFor="phone">New Phone Number</label>
              <input
                type="tel"
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
              />
            </div>
            <button type="submit" disabled={isLoading}>
              {isLoading ? 'Updating...' : 'Update Phone'}
            </button>
          </form>
          {success && success.includes('Phone') && (
            <div className="success-message">{success}</div>
          )}
        </div>
        )}

        <div className="account-section">
          <h2>Update Password</h2>
          <form onSubmit={handlePasswordUpdate}>
            <div className="form-group">
              <label htmlFor="currentPassword">Current Password</label>
              <input
                type="password"
                id="currentPassword"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                type="password"
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>
            <button type="submit" disabled={isLoading}>
              {isLoading ? 'Updating...' : 'Update Password'}
            </button>
          </form>
          {success && success.includes('Password') && (
            <div className="success-message">{success}</div>
          )}
        </div>

        {/* Biometric Settings - Only show on native platforms */}
        {Capacitor.isNativePlatform() && biometricAvailable && (
          <div className="account-section">
            <h2>Face ID Login</h2>
            <div className="biometric-settings">
              {biometricEnabled ? (
                <>
                  <p>Face ID login is currently enabled. You can use Face ID to sign in quickly.</p>
                  <button 
                    type="button"
                    className="disable-biometric-button"
                    onClick={handleDisableBiometric}
                    disabled={isDisablingBiometric}
                  >
                    {isDisablingBiometric ? 'Disabling...' : 'Disable Face ID Login'}
                  </button>
                </>
              ) : (
                <p>Face ID login is not enabled. You can enable it after your next login.</p>
              )}
            </div>
            {success && success.includes('Face ID') && (
              <div className="success-message">{success}</div>
            )}
            {error && error.includes('Face ID') && (
              <div className="error-message">{error}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default MyAccount; 